import { Component, OnInit } from '@angular/core';
import {IMyDrpOptions} from 'mydaterangepicker';
import * as moment from 'moment';
import {ApiService} from '../../../shared/services/api.service';
import {EnergyConsumption} from '../../../entities/energy-consumption.model';
import {ExcelService} from '../../../shared/services/excel.service';
import {PLAUSIBLE_ENERGY_CONSUMPTION_SU, PLAUSIBLE_ENERGY_CONSUMPTION_SQM} from '../../../app.constants';

@Component({
  selector: 'app-energy-consumption',
  templateUrl: './energy-consumption.component.html',
  styles: []
})
export class EnergyConsumptionComponent implements OnInit {

  public dateRangePickerOptions: IMyDrpOptions;
  public dateRangeModel;
  public loading = false;
  public dateChanged = false;

  public rows: EnergyConsumption[] = [];
  public filteredRows: EnergyConsumption[] = [];

  public filterMarketName = '';
  public filterClassification = '';
  public filterBranchName = '';
  public filterWeekday = '';
  public branches: string[] = [];

  public filterPlausibleOptions = ['egal', 'Ja', 'Nein'];
  public filterPlausibleConsumptionSu: 'egal' | 'Ja' | 'Nein' = 'egal';
  public filterPlausibleConsumptionSqm: 'egal' | 'Ja' | 'Nein' = 'egal';
  public classifications: string[] = [];

  public weekdays: string[] = [
    'Montag',
    'Dienstag',
    'Mittwoch',
    'Donnerstag',
    'Freitag',
    'Samstag',
    'Sonntag'
  ];

  plausibleConsumptionPerSalesunitLimit = PLAUSIBLE_ENERGY_CONSUMPTION_SU;
  plausibleConsumptionPerSquaremeter = PLAUSIBLE_ENERGY_CONSUMPTION_SQM;

  constructor(private apiService: ApiService, private excelService: ExcelService) {
    this.initDateRangePicker();
    this.reloadAndFilter();
    this.initBranchNumbers();
  }

  ngOnInit() {

  }

  /**
   * Loads api data based on date range from date range picker
   */
  public async load() {
    this.loading = true;
    const startDate = this.dateRangeModel.beginDate.year + '-' + this.dateRangeModel.beginDate.month.toString().padStart(2, '0') + '-'
      + this.dateRangeModel.beginDate.day.toString().padStart(2, '0');
    const endDate = this.dateRangeModel.endDate.year + '-' + this.dateRangeModel.endDate.month.toString().padStart(2, '0') + '-'
      + this.dateRangeModel.endDate.day.toString().padStart(2, '0');

    const data = await this.apiService.find<EnergyConsumption>
    (EnergyConsumption, 'backoffice/energy-consumption/' + startDate + '/' + endDate).toPromise().catch(e => this.loading = false);
    if (!data || typeof data !== 'object') { return; }
    // sort by date asc
    data.sort((a, b) => {
      if (a.evasiveDate < b.evasiveDate) { return 1; }
      if (a.evasiveDate > b.evasiveDate) { return -1; }
      return 0;
    });
    // calculate average energy consumptions
    data.forEach((item) => {

      let halfOrFullDay;
      switch (item.classification) {
        case 'Nachmittagsmarkt':
        case 'Vormittagsmarkt':
          halfOrFullDay = 'halfDay';
          break;
        case 'Ganztagsmarkt':
        default:
          halfOrFullDay = 'fullDay';
          break;
      }
      item.weightedEnergyUnits = item.boothsWithLowEnergy + (item.boothsWithNormalEnergy * 2) + (item.boothsWithSpecialEnergy * 3);

      // (getTotalKwh() / getWeightetEnergyUnits()) / (openingDuration / 60) | number:'1.3'}} kWh
      item.weightedEnergyAvgVe = item.energyTotal / item.weightedEnergyUnits;
      item.energyAvgVe = item.energyTotal / item.boothsWithEnergy;

      item.energyAvgVePlausible = (item.energyAvgVe) ?
        (
          (
            ((item.energyTotal / item.boothsWithEnergy) / (item.openingDuration / 60))
            >= (this.plausibleConsumptionPerSalesunitLimit[halfOrFullDay].low / (item.openingDuration / 60))
            && ((item.energyTotal / item.boothsWithEnergy) / (item.openingDuration / 60))
            <= (this.plausibleConsumptionPerSalesunitLimit[halfOrFullDay].high / (item.openingDuration / 60))
          ) ? 'Ja' : 'Nein'
        ) : '---';

      item.energyAvgSqm = item.energyTotal / item.area;
      item.energyAvgSqmPlausible = (item.energyAvgVe) ?
        (
          (
            ((item.energyTotal / item.area) / (item.openingDuration / 60))
            >= (this.plausibleConsumptionPerSquaremeter[halfOrFullDay].low / (item.openingDuration / 60))
            && ((item.energyTotal / item.area) / (item.openingDuration / 60))
            <= (this.plausibleConsumptionPerSquaremeter[halfOrFullDay].high  / (item.openingDuration / 60))
          ) ? 'Ja' : 'Nein'
        ) : '---';

      // Check for invalid energy data
      if (item.classification !== 'kein Stromzähler' && item.energyTotal === 0 && item.boothsWithEnergy > 0) {
        item.classification = 'fehlerhafte Erfassung';
      }

      if (!this.classifications.some(val => val === item.classification)) {
        this.classifications.push(item.classification);
      }
    });
    this.rows = data;
    this.filteredRows = this.rows.filter(() => true);
    this.loading = false;
  }

  /**
   * Initializes date range picker
   */
  initDateRangePicker() {
    const startDate = moment();
    startDate.subtract(13, 'days');
    const endDate = moment();
    this.dateRangePickerOptions = {
      dayLabels: {su: 'So', mo: 'Mon', tu: 'Di', we: 'Mi', th: 'Do', fr: 'Fr', sa: 'Sa'},
      monthLabels: { 1: 'Jan', 2: 'Feb', 3: 'Mrz', 4: 'Apr', 5: 'Mai', 6: 'Jun', 7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Okt',
        11: 'Nov', 12: 'Dez' },
      dateFormat: 'dd.mm.yyyy',
      yearSelector: false,
      monthSelector: false,
      selectBeginDateTxt: 'Startdatum auswählen',
      selectEndDateTxt: 'Enddatum auswählen',
      showClearBtn: true,
      showApplyBtn: true,
      openSelectorOnInputClick: true,
      height: '26px',
      width: '239px'
    };
    this.dateRangeModel = {
      beginDate: {
        year: startDate.year(),
        month: startDate.month(),
        day: 1
      },
      endDate: {
        year: endDate.year(),
        month: endDate.month() + 1,
        day: endDate.date()
      }
    };
  }

  /**
   * Resets all filters
   */
  initFilter() {
    this.filterMarketName = '';
    this.filterClassification = '';
    this.filterBranchName = '';
    this.filterWeekday = '';
    this.filterPlausibleConsumptionSu = 'egal';
    this.filterPlausibleConsumptionSqm = 'egal';
  }

  async initBranchNumbers() {
    const branches = await this.apiService.getBranches();
    branches.forEach(branch => {
      this.branches.push(branch.name);
    });
  }

  /**
   * Resets filtered rows & applies all filters
   */
  applyFilters() {
    // reset filter
    this.filteredRows = this.rows.filter(() => true);

    if (this.filterMarketName.length > 0) {
      this.filteredRows = this.filteredRows.filter((item) =>
        item.keyword.toLowerCase().includes(this.filterMarketName.toLowerCase())
        || item.marketdayNumber.toLowerCase().includes(this.filterMarketName.toLowerCase())
      );
    }

    if (this.filterWeekday.length > 0) {
      this.filteredRows = this.filteredRows.filter((item) => item.dayString === this.filterWeekday);
    }

    if (this.filterClassification.length > 0) {
      this.filteredRows = this.filteredRows.filter((item) =>
        item.classification.toLowerCase().includes(this.filterClassification.toLowerCase()));
    }

    if (this.filterBranchName.length > 0) {
      this.filteredRows = this.filteredRows.filter((item) => item.branchNumber.includes(this.filterBranchName.split(' - ')[0]));
    }

    if (this.filterPlausibleConsumptionSu !== 'egal') {
      this.filteredRows = this.filteredRows.filter((item) => item.energyAvgVePlausible === this.filterPlausibleConsumptionSu);
    }

    if (this.filterPlausibleConsumptionSqm !== 'egal') {
      this.filteredRows = this.filteredRows.filter((item) => item.energyAvgSqmPlausible === this.filterPlausibleConsumptionSqm);
    }
  }

  /**
   * Reloads data from api and applies filters after reload
   */
  async reloadAndFilter() {
    await this.load();
    this.initFilter();
    this.applyFilters();
  }

  /**
   * Saves the filtered meterreadings as csv file
   */
  saveToCSV() {
    const headers = [
      'Datum',
      'Marktnummer',
      'Marktplatz',
      'Wochentag',
      'Klassifizierung',
      'Öffnungsdauer',
      'Kürzel NL',
      'Tagesverbrauch in kWh',
      'Anzahl der tatsächlich anwesenden VEs mit Stromverbrauch in kWh',
      'Summe der gewichteten Energieeinheiten',
      'Summe der tatsächlich belegten Fläche mit Stromverbrauch in kWh',
      'Durchschnittlicher Verbrauch je VE / Std',
      'Durchschnittlicher Verbrauch je gewichteter Energieeinheit / Std',
      'Plausibel 1',
      'Durchschnittlicher Verbrauch je qm / Std',
      'Plausibel 2'
    ];
    const jsonTable = [];
    this.filteredRows.forEach(
      row => {
        jsonTable.push({
          'Datum' : row.evasiveDate,
          'Marktnummer': row.marketdayNumber,
          'Marktplatz' : row.keyword,
          'Wochentag': row.dayString,
          'Klassifizierung': row.classification,
          'Oeffnungsdauer': row.openingDuration,
          'Kuerzel NL': row.branchNumber,
          'Tagesverbrauch in kWh': (row.energyTotal != null) ? Math.round(row.energyTotal * 1000) / 1000 : 0,
          'Anzahl der tatsaechlich anwesenden VEs mit Stromverbrauch in kWh': row.boothsWithEnergy,
          'Summe der gewichteten Energieeinheiten': row.weightedEnergyUnits,
          'Summe der tatsaechlich belegten Flaeche mit Stromverbrauch in kWh': row.area,
          'Durchschnittlicher Verbrauch je VE / Std': (row.energyAvgVe && row.boothsWithEnergy > 0) ?
            Math.round((row.energyAvgVe / (row.openingDuration / 60)) * 1000) / 1000 : 0,
          'Durchschnittlicher Verbrauch je gewichteter Energieeinheit / Std': (row.weightedEnergyAvgVe && row.boothsWithEnergy > 0) ?
            Math.round((row.weightedEnergyAvgVe / (row.openingDuration / 60)) * 1000) / 1000 : 0,
          'Plausibel 1': row.energyAvgVePlausible,
          'Durchschnittlicher Verbrauch je qm / Std': (row.energyAvgSqm && row.boothsWithEnergy > 0) ?
            Math.round((row.energyAvgSqm / (row.openingDuration / 60)) * 1000) / 1000 : 0,
          'Plausibel 2': row.energyAvgSqmPlausible,
        });
      }
    );
    const header = {header: headers};
    this.excelService.exportAsExcelFile(jsonTable, header, 'Übersicht-Stromverbrauch-Markttage');
  }

  /**
   * Returns weekday string from date
   */
  getWeekday(date: any) {
    switch (moment(date).weekday()) {
      case 1: return 'Montag';
      case 2: return 'Dienstag';
      case 3: return 'Mittwoch';
      case 4: return 'Donnerstag';
      case 5: return 'Freitag';
      case 6: return 'Samstag';
      case 7: return 'Sonntag';
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/evaluation/components/power-consumption-markets/energy-consumption.component.ts